import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api.js";

// Global programme context. Holds:
//   * programmes — the list the tenant has enabled (server-authoritative)
//   * activeSlug — the one currently in focus across the UI
//
// Persisted in localStorage so a page refresh keeps the switcher pinned on
// the same programme. If the persisted slug isn't in the fetched list
// anymore (tenant disabled it), we fall back to the first enabled
// programme. If the fetch fails entirely, we default to fs_english so the
// existing product still works offline.

export type ProgrammeSlug = "fs_english" | "heda";

export interface Programme {
  id: string;
  slug: ProgrammeSlug;
  name: string;
  subject_labels: string[];
  level_codes: string[];
  is_active: boolean;
}

interface Ctx {
  loading: boolean;
  programmes: Programme[];
  active: Programme | null;
  setActive: (slug: ProgrammeSlug) => void;
}

const ACTIVE_KEY = "exambase.active_programme";

const ProgrammeContext = createContext<Ctx | null>(null);

// Fallback the web uses before any programme API call resolves. Matches the
// fs_english seed in master migration 0006 so list-endpoint filters still
// match on an id.
const FALLBACK_FS_ENGLISH: Programme = {
  id: "prog_fs_english_0000",
  slug: "fs_english",
  name: "Functional Skills English",
  subject_labels: ["reading", "writing", "speaking", "listening"],
  level_codes: ["E1", "E2", "E3", "L1", "L2"],
  is_active: true,
};

export function ProgrammeProvider({ children }: { children: ReactNode }) {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [activeSlug, setActiveSlug] = useState<ProgrammeSlug>(() => {
    const stored = typeof localStorage !== "undefined"
      ? (localStorage.getItem(ACTIVE_KEY) as ProgrammeSlug | null)
      : null;
    return stored === "heda" || stored === "fs_english" ? stored : "fs_english";
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void api
      .listOrgProgrammes()
      .then((res) => {
        if (!alive) return;
        setProgrammes(res.programmes);
        // If the persisted active slug isn't in the fetched list, pick the
        // first enabled programme instead.
        if (!res.programmes.some((p) => p.slug === activeSlug)) {
          const first = res.programmes[0];
          if (first) {
            setActiveSlug(first.slug);
            localStorage.setItem(ACTIVE_KEY, first.slug);
          }
        }
      })
      .catch(() => {
        // Silent fallback — programmes drive gating, not core auth. Keep
        // fs_english available so existing product paths still work.
        if (alive) setProgrammes([FALLBACK_FS_ENGLISH]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setActive = useCallback((slug: ProgrammeSlug) => {
    setActiveSlug(slug);
    localStorage.setItem(ACTIVE_KEY, slug);
  }, []);

  const active = useMemo(
    () =>
      programmes.find((p) => p.slug === activeSlug) ??
      programmes[0] ??
      FALLBACK_FS_ENGLISH,
    [programmes, activeSlug],
  );

  return createElement(
    ProgrammeContext.Provider,
    { value: { loading, programmes, active, setActive } },
    children,
  );
}

export function useProgramme(): Ctx {
  const ctx = useContext(ProgrammeContext);
  if (!ctx) {
    return {
      loading: false,
      programmes: [FALLBACK_FS_ENGLISH],
      active: FALLBACK_FS_ENGLISH,
      setActive: () => undefined,
    };
  }
  return ctx;
}
